import { Box, CircularProgress, Typography } from "@mui/material";
import TwoWheelerIcon from "@mui/icons-material/TwoWheeler";
import {
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import React, { useCallback, useEffect, useState } from "react";
import { getDeliveryServiceStats } from "../../../../api/DeliveryAnalyticsService";

const COLORS = [
  "#0088FE",
  "#00C49F",
  "#FFBB28",
  "#FF8042",
  "#8884d8",
  "#82ca9d",
];

export const DeliveryServiceStats = ({ startDate, endDate }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadData();
  }, [loadData, startDate, endDate]);

  const loadData = useCallback(async () => {
    if (!startDate || !endDate) return;

    setLoading(true);
    setError(null);

    try {
      // Create params object for API calls
      const params = {
        start_date: startDate,
        end_date: endDate,
      };

      // Call the stats API
      const response = await getDeliveryServiceStats(params);

      // Set stats data directly from API response
      setData(response.data);
    } catch (err) {
      console.error("Error loading order stats data:", err);
      setError("Não foi possível carregar os dados de pedidos");
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: 300,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Calculate total orders for percentage
  const totalOrders = data.reduce((sum, item) => sum + item.orderCount, 0);

  // Add percentage to data for better visualization
  const dataWithPercentage = data.map((item) => ({
    ...item,
    percentage: (item.orderCount / totalOrders) * 100,
  }));

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <TwoWheelerIcon sx={{ mr: 1, color: "warning.main" }} />
        <Typography variant="h6">Serviços de Entrega</Typography>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Distribuição de pedidos por serviço de entrega
      </Typography>

      <Box sx={{ height: 250 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={dataWithPercentage}
              cx="50%"
              cy="50%"
              labelLine={false}
              outerRadius={80}
              fill="#8884d8"
              dataKey="orderCount"
              nameKey="serviceName"
              label={({ name, percent }) => `${name}: ${percent.toFixed(1)}%`}
            >
              {dataWithPercentage.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={COLORS[index % COLORS.length]}
                />
              ))}
            </Pie>
            <Tooltip
              formatter={(value, name, props) => [
                `${value} pedidos (${props.payload.percentage.toFixed(1)}%)`,
                "Quantidade",
              ]}
              labelFormatter={(value) => `Serviço: ${value}`}
            />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      </Box>

      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mt: 2, textAlign: "center" }}
      >
        Total de pedidos: {totalOrders.toLocaleString()}
      </Typography>
    </>
  );
};
